import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { ContentResourceLoader, ContentResourceLoaderProps, useScopedSelector } from 'ia-react-core';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { ResourceInfo } from 'omni-package-react-core';
import ActionBar from './ActionBar';
import Details from './Details';
import { Message } from '../../../interfaces/Message';
import { MessageDetailsPageState } from '../../../interfaces/MessageDetailsPageState';
import { MessageDetailDto } from '../../../interfaces/dtos/MessageDetailDto';
import getApiRoot from '../../../utils/getApiRoot';
import transformMessageDetail from '../../SecuredMessagingPage/utils/transformMessageDetail/transformMessageDetail';
import { setMessageRead } from '../../SecuredMessagingPage/SecuredMessagingPage.actions';

export interface DetailsLoaderProps extends ContentResourceLoaderProps<Message> {
  messageId: string;
  messageSource: string;
}

export const MessageDetailsResourceInfo: ResourceInfo = {
  name: 'messageDetailsResource',
  dataDst: 'messageDetails',
};

const DetailsLoader: FC<DetailsLoaderProps> = ({ messageId, messageSource, ...props }) => {
  const dispatch = useDispatch();
  const url = useMemo(() => `${getApiRoot()}/messages/${messageId}?messageSource=${messageSource}`, [messageId, messageSource]);
  const messageDetails = useScopedSelector((state: MessageDetailsPageState) => state.messageDetails);
  const { t } = useTranslation('DetailsLoader');

  const postMessageRead = useCallback(() => {
    axios.post(
      `${getApiRoot()}/messages/${messageId}/markasread`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    ).catch(() => {
      // we don't catch those errors
    });

    dispatch(setMessageRead(Number(messageId)));
  }, [dispatch, messageId]);

  useEffect(() => {
    if (messageDetails?.isShouldMarkAsRead) { postMessageRead(); }
  }, [messageDetails?.isShouldMarkAsRead, postMessageRead]);

  return (
    <ContentResourceLoader
      {...MessageDetailsResourceInfo}
      requestOnLanguageChange
      url={url}
      config={{ withCredentials: true }}
      transformResponse={(message: MessageDetailDto) => transformMessageDetail(message, t)}
      buttonAlign="left"
      {...props}
    >
      <Details message={messageDetails?.data} />
      <ActionBar messageId={messageDetails?.data?.id} pagination={messageDetails?.pagination} />
    </ContentResourceLoader>
  );
};
export default DetailsLoader;
